import { Badge, Text } from '@mantine/core'
import { GenericTable, TableColumn } from './Table/GenericTable'
import { useTable } from './Table/useTable'
import { useCurrentShifts, CurrentShift } from '@/hooks/useCurrentShifts'
import { formatDateTime } from '@/utils/datetime'
import { useTranslation } from 'react-i18next'

export function CurrentShiftsTable() {
  const { t } = useTranslation('shifts')
  const { data: shifts = [], isLoading, error } = useCurrentShifts()

  const table = useTable<CurrentShift>({
    data: shifts,
    defaultSortField: 'start',
    defaultSortDirection: 'asc',
    itemsPerPage: Infinity,
    tableKey: 'currentShiftsTable',
    columns: [
      {
        key: 'vehicle_id',
        label: t('vehicle'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text fw={500}>{shift.vehicle_id}</Text>,
      },
      {
        key: 'driver',
        label: t('driver'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text>{shift.driver || 'N/A'}</Text>,
      },
      {
        key: 'start',
        label: t('startTime'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text>{formatDateTime(shift.start)}</Text>,
      },
      {
        key: 'end',
        label: t('endTime'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => <Text>{formatDateTime(shift.end)}</Text>,
      },
      {
        key: 'status',
        label: t('status'),
        visible: true,
        sortable: true,
        render: (shift: CurrentShift) => {
          const statusConfig = {
            running: { color: 'green', label: t('running') },
            upcoming: { color: 'blue', label: t('upcoming') },
            completed: { color: 'gray', label: t('completed') },
          }
          const config = statusConfig[shift.status]
          return (
            <Badge variant="light" color={config.color}>
              {config.label}
            </Badge>
          )
        },
      },
    ] as TableColumn<CurrentShift>[],
  })

  return (
    <GenericTable
      {...table.tableProps}
      // Keep the inner max-height scroll container (this table has no pagination).
      filteredData={undefined}
      isLoading={isLoading}
      error={error}
      hideConfig
      hideCsvDownload
      getRowKey={shift => shift.id.toString()}
      emptyMessage={t('noCurrentOrUpcomingShiftsFound')}
    />
  )
}
